/**
 * AnimationBackdrop - Dark overlay with optional revealed-item cover
 *
 * Renders a semi-transparent dark tint whenever an animation step
 * requires a dimmed background (item-appear, educational, etc.).
 *
 * When an item has just been collected, it is also rendered inside this
 * backdrop at full brightness. Because both the tint and the item live
 * inside the same motion.div, they share a single exit animation.
 *
 * Alpha-compositing guarantees seamless brightness:
 *   backdrop item (fading) + background item (constant) = constant brightness
 * The dark tint fades independently around the item.
 *
 * This eliminates the flash that occurred when the dark overlay and the
 * item-appear animation were separate elements with independent lifetimes.
 */

import { useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useCurrentPhaseItems, useCurrentRoom, useDarkOverlayVisible } from "@/state/selectors";
import { useGameStore } from "@/state/gameStore";
import { getItemAssetVariant } from "@/utils/itemUtils";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { CoverCanvas } from "@/components/shared/CoverCanvas";
import { getFormatKey } from "@/libs/assetPreloading/preloadConfig";

const backdropVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.35, ease: "easeInOut" } },
} as const;

export function AnimationBackdrop() {
    const darkOverlayVisible = useDarkOverlayVisible();
    const revealedItemId = useGameStore((s) => s.revealedItemId);
    const clearRevealedItemId = useGameStore((s) => s.clearRevealedItemId);
    const roomVariant = useGameStore((s) => s.currentRoomVariant);
    const room = useCurrentRoom();
    const allPhaseItems = useCurrentPhaseItems();

    // Keep a ref so the item persists in the component during
    // the exit animation even if the store is cleared externally.
    const revealedItemIdRef = useRef<string | null>(null);
    if (revealedItemId) {
        revealedItemIdRef.current = revealedItemId;
    }

    const itemId = revealedItemIdRef.current;
    const revealedItem = itemId ? allPhaseItems.find((i) => i.documentId === itemId) : null;
    const revealedImage = revealedItem && roomVariant ? getItemAssetVariant(revealedItem, roomVariant) : null;

    const handleExitComplete = useCallback(() => {
        revealedItemIdRef.current = null;
        clearRevealedItemId();
    }, [clearRevealedItemId]);

    const format = revealedImage?.formats?.[getFormatKey("lg")];
    const imageW = format?.width ?? revealedImage?.width;
    const imageH = format?.height ?? revealedImage?.height;

    return (
        <AnimatePresence onExitComplete={handleExitComplete}>
            {darkOverlayVisible && (
                <motion.div
                    key="animation-backdrop"
                    variants={backdropVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="absolute inset-0 z-10"
                >
                    {/* Dark tint — always present when backdrop is mounted */}
                    <div className="absolute inset-0 bg-black/35" />

                    {/*
                     * Revealed item — rendered at full brightness on top of the tint.
                     * Because this lives inside the same motion.div, it fades out
                     * in lockstep with the tint. The identical item in RoomBackground
                     * underneath ensures constant perceived brightness.
                     */}
                    {revealedImage && imageW && imageH && (
                        <CoverCanvas imageW={imageW} imageH={imageH} className="absolute inset-0">
                            <StrapiMedia
                                size="lg"
                                image={revealedImage}
                                alt={revealedImage.url}
                                className="absolute inset-0 size-full"
                                objectFit="fill"
                                useImageAspectRatio={false}
                                priority
                            />
                        </CoverCanvas>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
